import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    confirmed: 0,
    preparing: 0,
    shipped: 0,
    delivered: 0,
    cancelled: 0
  });

  useEffect(() => {
    fetchOrders();
  }, [filter]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/orders', {
        params: { status: filter !== 'all' ? filter : undefined, limit: 100 }
      });
      setOrders(response.data.orders || []);
      
      // Calculer les stats
      const allOrders = response.data.orders || [];
      setStats({
        total: allOrders.length,
        pending: allOrders.filter(o => o.status === 'pending').length,
        confirmed: allOrders.filter(o => o.status === 'confirmed').length,
        preparing: allOrders.filter(o => o.status === 'preparing').length,
        shipped: allOrders.filter(o => o.status === 'shipped').length,
        delivered: allOrders.filter(o => o.status === 'delivered').length,
        cancelled: allOrders.filter(o => o.status === 'cancelled').length
      });
    } catch (error) {
      console.error('Erreur chargement commandes:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/admin/orders/${id}/status`, { status });
      fetchOrders();
    } catch (error) {
      console.error('Erreur mise à jour statut:', error);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-600',
      confirmed: 'bg-blue-100 text-blue-600',
      preparing: 'bg-purple-100 text-purple-600',
      shipped: 'bg-indigo-100 text-indigo-600',
      delivered: 'bg-green-100 text-green-600',
      cancelled: 'bg-red-100 text-red-600'
    };
    const labels = {
      pending: 'En attente',
      confirmed: 'Confirmée',
      preparing: 'Préparation',
      shipped: 'Expédiée',
      delivered: 'Livrée',
      cancelled: 'Annulée'
    };
    return <span className={`px-2 py-1 rounded-full text-xs ${badges[status] || 'bg-gray-100 text-gray-600'}`}>{labels[status] || status}</span>;
  };

  const getPaymentBadge = (status) => {
    return status === 'paid' 
      ? <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-600">Payé</span>
      : <span className="px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-600">En attente</span>;
  };

  const filteredOrders = orders.filter(order => {
    const matchSearch = order.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        order.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        order.customer_email?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchSearch;
  });

  const statuses = [
    { value: 'all', label: 'Toutes' },
    { value: 'pending', label: 'En attente' },
    { value: 'confirmed', label: 'Confirmée' },
    { value: 'preparing', label: 'Préparation' },
    { value: 'shipped', label: 'Expédiée' },
    { value: 'delivered', label: 'Livrée' },
    { value: 'cancelled', label: 'Annulée' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-500">Chargement des commandes...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Gestion des commandes</h1>
          <p className="text-gray-500 mt-1">Gérez et suivez toutes les commandes</p>
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-2 md:grid-cols-7 gap-3 mb-6">
        <div className="bg-white rounded-xl shadow-md p-3 text-center">
          <p className="text-xl font-bold">{stats.total}</p>
          <p className="text-xs text-gray-500">Total</p>
        </div>
        <div className="bg-yellow-50 rounded-xl shadow-md p-3 text-center border-l-4 border-yellow-500">
          <p className="text-xl font-bold text-yellow-600">{stats.pending}</p>
          <p className="text-xs">En attente</p>
        </div>
        <div className="bg-blue-50 rounded-xl shadow-md p-3 text-center border-l-4 border-blue-500">
          <p className="text-xl font-bold text-blue-600">{stats.confirmed}</p>
          <p className="text-xs">Confirmée</p>
        </div>
        <div className="bg-purple-50 rounded-xl shadow-md p-3 text-center border-l-4 border-purple-500">
          <p className="text-xl font-bold text-purple-600">{stats.preparing}</p>
          <p className="text-xs">Préparation</p>
        </div>
        <div className="bg-indigo-50 rounded-xl shadow-md p-3 text-center border-l-4 border-indigo-500">
          <p className="text-xl font-bold text-indigo-600">{stats.shipped}</p>
          <p className="text-xs">Expédiée</p>
        </div>
        <div className="bg-green-50 rounded-xl shadow-md p-3 text-center border-l-4 border-green-500">
          <p className="text-xl font-bold text-green-600">{stats.delivered}</p>
          <p className="text-xs">Livrée</p>
        </div>
        <div className="bg-red-50 rounded-xl shadow-md p-3 text-center border-l-4 border-red-500">
          <p className="text-xl font-bold text-red-600">{stats.cancelled}</p>
          <p className="text-xs">Annulée</p>
        </div>
      </div>

      {/* Filtres et recherche */}
      <div className="bg-white rounded-xl shadow-md p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div className="flex gap-2 flex-wrap">
            {statuses.map(s => (
              <button
                key={s.value}
                onClick={() => setFilter(s.value)}
                className={`px-4 py-2 rounded-lg transition ${filter === s.value ? 'bg-primary text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div className="relative">
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border rounded-lg w-64 focus:outline-none focus:border-secondary"
            />
            <svg className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
      </div>

      {/* Tableau des commandes */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold">N° Commande</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Client</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Date livraison</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Total</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Paiement</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Statut</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    Aucune commande trouvée
                  </td>
                </tr>
              ) : (
                filteredOrders.map(order => (
                  <tr key={order.id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-sm font-bold">{order.order_number}</td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium">{order.customer_name}</p>
                        <p className="text-xs text-gray-500">{order.customer_email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {order.delivery_date 
                        ? new Date(order.delivery_date).toLocaleDateString('fr-FR') 
                        : 'Non définie'}
                    </td>
                    <td className="px-4 py-3 font-bold">{order.grand_total} €</td>
                    <td className="px-4 py-3">{getPaymentBadge(order.payment_status)}</td>
                    <td className="px-4 py-3">{getStatusBadge(order.status)}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => setSelectedOrder(order)} className="text-secondary hover:underline text-sm">
                          Détails
                        </button>
                        <select
                          value={order.status}
                          onChange={(e) => updateStatus(order.id, e.target.value)}
                          className="px-2 py-1 border rounded text-sm"
                        >
                          {statuses.filter(s => s.value !== 'all').map(s => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </select>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Détails commande */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto m-4">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold">Détails commande #{selectedOrder.order_number}</h2>
                <button onClick={() => setSelectedOrder(null)} className="text-gray-500 hover:text-gray-700 text-2xl">✕</button>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Informations client</h3>
                  <p><strong>Nom :</strong> {selectedOrder.customer_name}</p>
                  <p><strong>Email :</strong> {selectedOrder.customer_email}</p>
                  <p><strong>Téléphone :</strong> {selectedOrder.customer_phone || 'Non renseigné'}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Livraison</h3>
                  <p><strong>Adresse :</strong> {selectedOrder.delivery_address}</p>
                  <p><strong>Ville :</strong> {selectedOrder.delivery_city}</p>
                  <p><strong>Code postal :</strong> {selectedOrder.delivery_postal}</p>
                  <p><strong>Date :</strong> {selectedOrder.delivery_date ? new Date(selectedOrder.delivery_date).toLocaleDateString('fr-FR') : 'Non définie'}</p>
                  <p><strong>Créneau :</strong> {selectedOrder.delivery_slot || 'Non spécifié'}</p>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="font-semibold mb-2">Récapitulatif</h3>
                <div className="flex justify-between mb-2">
                  <span>Sous-total</span>
                  <span>{selectedOrder.total_amount} €</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span>Livraison</span>
                  <span>{selectedOrder.delivery_fee} €</span>
                </div>
                <div className="flex justify-between font-bold text-lg pt-2 border-t">
                  <span>Total</span>
                  <span className="text-primary">{selectedOrder.grand_total} €</span>
                </div>
              </div>

              <div className="flex gap-3">
                <button className="flex-1 bg-primary text-white py-2 rounded-lg">Imprimer la facture</button>
                <button className="flex-1 bg-secondary text-primary py-2 rounded-lg">Télécharger PDF</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;